import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckboxList,
  DialogOverlayActions,
  Ellipsis,
  ListItem,
  Skeleton,
  TransitioningDialogOverlay,
  usePageWidth,
  type useOverlay,
} from '@fs/zion-ui'
import { Route, useHistory } from '@fs/zion-router'
import { ContentAdd } from '@fs/zion-icon'
import playlistsConfig from '../../../../config/feature/playlist'
import PillButton from '../../../../lib/buttons/PillButton'
import UpdatePlaylistForm from '../UpdatePlaylistForm'
import { usePlaylists } from '../../../../hooks/playlist/usePlaylists'
import { usePlaylistCreation } from '../../../../hooks/playlist/usePlaylistCreation'
import { usePlaylistsByItems } from '../../../../hooks/playlist/usePlaylistsByItems'
import { usePlaylistUpdate } from '../../../../hooks/playlist/usePlaylistUpdate'
import { useFlagsContext, usePlaylistContainerContext } from '../../../../providers'
import { FLAGS } from '../../../../config'
import notifySentry from '../../../../lib/helpers/sentryNotifier'

const Loading = ({ count = 3 }): JSX.Element => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Skeleton.Checkbox key={index} />
    ))}
  </>
)

/**
 * `CheckboxList` builds each row's `Checkbox` itself, forwarding only `name`, `primaryText`
 * and `disabled` off the child, so a `disabledDescription` set on a plain `ListItem` never
 * reaches the input. Standing in `ListItem`'s place lets us clone the injected `Checkbox` and
 * supply one. Zion's `Checkbox` then swaps the native `disabled` attribute for `aria-disabled`,
 * which keeps a disabled row in the tab order and announces why it cannot be chosen, while
 * still swallowing its click and change events.
 */
const ListItemWithDisabledReason = ({
  disabledDescription,
  startElement,
  ...listItemProps
}: {
  disabledDescription?: string
  startElement?: React.ReactNode
  [prop: string]: unknown
}): JSX.Element => (
  <ListItem
    {...listItemProps}
    startElement={
      disabledDescription && React.isValidElement(startElement)
        ? React.cloneElement(startElement, { disabledDescription })
        : startElement
    }
  />
)

const UpdatePlaylist = ({
  sessionId,
  onPlaylistUpdated,
}: {
  sessionId: string
  onPlaylistUpdated?: (playlistIds: string[]) => void
}): JSX.Element => {
  const [t] = useTranslation()
  const { [FLAGS.NEW_PLAYLIST_STATE]: newPlaylistStateFlag } = useFlagsContext()

  // New stack (React Query)
  const { playlists: nPlaylists } = usePlaylists()
  const { playlistsByItems, isLoading } = usePlaylistsByItems(newPlaylistStateFlag.isOn ? [sessionId] : [])
  const { updatePlaylist } = usePlaylistUpdate()

  // Old stack (context)
  const {
    playlists: cPlaylistsRaw,
    findPlaylistsWithItem,
    addToPlaylist,
    removeFromPlaylist,
  } = usePlaylistContainerContext()
  const cPlaylists = cPlaylistsRaw ?? new Map()
  const oldSelectedIdsRef = useRef<Set<string>>(new Set())
  const [oldLoading, setOldLoading] = useState(!newPlaylistStateFlag.isOn)
  useEffect(() => {
    if (newPlaylistStateFlag.isOn) return
    findPlaylistsWithItem({ itemId: sessionId }).then((results: { id: string; parent?: unknown }[]) => {
      oldSelectedIdsRef.current = new Set(results.filter((p) => !p.parent).map((p) => p.id))
      setOldLoading(false)
    })
  }, [newPlaylistStateFlag.isOn, findPlaylistsWithItem, sessionId])

  const playlists = newPlaylistStateFlag.isOn ? nPlaylists : [...cPlaylists.values()].filter((p) => !p.parent)

  // Mirrors the new stack's accepted selection so the no-op guard below reads it at
  // execution time rather than capturing a stale copy when the toggle was enqueued.
  const nSelectedIds = newPlaylistStateFlag.isOn ? (playlistsByItems[sessionId]?.results.map(({ id }) => id) ?? []) : []
  const nSelectedIdsRef = useRef<string[]>(nSelectedIds)
  nSelectedIdsRef.current = nSelectedIds

  // Toggles are serialized through this queue so rapid keyboard input still applies in the
  // order it was entered. The touched row used to be `disabled` while its request was in
  // flight, but Zion renders that as a native `disabled` attribute and browsers blur a
  // focused element the instant it becomes disabled, stranding keyboard users (RT-103).
  const queueRef = useRef<Promise<boolean>>(Promise.resolve(false))

  const onChange = useCallback(
    async (newSelectedIds: string[], lastTouched: { name: string; checked: boolean }): Promise<void> => {
      if (!lastTouched) return
      const { name: playlistId, checked } = lastTouched

      // Resolves true only when the toggle actually changed something. `useCheckboxList`
      // lists `onSelectionChange` in its notify effect's deps and never clears
      // `lastTouchedRef`, so a new `onChange` identity re-fires this callback with the same
      // `lastTouched`. Consumers pass an inline `onPlaylistUpdated` (PlaylistDetails) that
      // triggers a refresh, which re-renders and mints a new identity — notifying on a
      // no-op closes that circuit into an unbounded loop.
      const applyChange = async (): Promise<boolean> => {
        if (newPlaylistStateFlag.isOn) {
          if (checked === nSelectedIdsRef.current.includes(playlistId)) return false
          try {
            await updatePlaylist(
              checked ? { id: playlistId, addItems: [sessionId] } : { id: playlistId, removeItems: [sessionId] }
            )
          } catch (err) {
            // Zion invokes `onSelectionChange` from an effect without awaiting it, so an
            // escaping rejection here surfaced as an unhandled promise rejection.
            notifySentry(err)
            return false
          }
          return true
        }
        const alreadyChecked = oldSelectedIdsRef.current.has(playlistId)
        if (checked === alreadyChecked) return false
        const previousIds = oldSelectedIdsRef.current
        try {
          if (checked) {
            oldSelectedIdsRef.current = new Set(Array.from(oldSelectedIdsRef.current).concat(playlistId))
            await addToPlaylist({ playlistId, items: [sessionId] })
          } else {
            oldSelectedIdsRef.current = new Set(Array.from(oldSelectedIdsRef.current).filter((id) => id !== playlistId))
            await removeFromPlaylist({ playlistId, itemId: sessionId })
          }
        } catch (err) {
          oldSelectedIdsRef.current = previousIds
          notifySentry(err)
          // The optimistic ref was rolled back, so nothing changed — don't notify.
          return false
        }
        return true
      }

      // Chained on both settle paths so one failed toggle cannot wedge the queue.
      queueRef.current = queueRef.current.then(applyChange, applyChange)
      if (await queueRef.current) onPlaylistUpdated?.(newSelectedIds)
    },
    [newPlaylistStateFlag.isOn, updatePlaylist, addToPlaylist, removeFromPlaylist, sessionId, onPlaylistUpdated]
  )

  // Only the initial load shows skeletons. Gating on `isFetching` too would tear the
  // focused checkbox down with the list on any background refetch. Defensive today —
  // these queries are `staleTime: Infinity` and the mutations write through the cache
  // rather than invalidating it, so `isFetching` effectively only rises on first fetch.
  if ((newPlaylistStateFlag.isOn && isLoading) || oldLoading) {
    return <Loading count={playlists.length} />
  }

  const selectedIds = newPlaylistStateFlag.isOn ? nSelectedIds : Array.from(oldSelectedIdsRef.current)

  // Keyed on the playlist set only, never on the selection: re-keying on the selection
  // remounted the entire list after every toggle, destroying the focused input (RT-103).
  // CheckboxList owns its own checked state once mounted, so it needs no such nudge.
  const checkboxKey = playlists.map((p) => p.id).join(',')

  return (
    <CheckboxList onSelectionChange={onChange} initialSelection={selectedIds} key={checkboxKey}>
      {playlists
        .filter((p) => !p.parent)
        .map((playlist) => {
          const full = !playlist.default && playlist.size >= playlistsConfig.sessionLimit

          let secondaryText = t('playlists.playlist.size', '{ playlistSize } videos', {
            playlistSize: playlist.size || 0,
          })
          if (full) {
            secondaryText = t('playlists.playlists.size-status', '{ playlistSize } videos (FULL)', {
              playlistSize: playlist.size,
            })
          }

          // Being full only blocks *adding*. Removing this video is always legitimate, and is
          // exactly what the reason text tells the user to do, so a full playlist that already
          // holds the session stays operable instead of trapping the one removal it could offer.
          const locked = full && !selectedIds.includes(playlist.id)

          // The visible "(FULL)" hint is the only signal a playlist cannot take more videos,
          // and a natively disabled input is never focused, so it was never announced. This
          // reason flips Zion's `Checkbox` to `aria-disabled`, which keeps the row reachable.
          const disabledDescription = locked
            ? t(
                'playlists.playlist.full.description',
                'This playlist is full. Playlists hold up to { maxSize } videos. Remove a video or choose another playlist.',
                { maxSize: playlistsConfig.sessionLimit }
              )
            : undefined

          return (
            <ListItemWithDisabledReason
              data-testid={`${playlist.default ? 'watch-later' : playlist.name}-playlist`}
              dense
              disabled={locked}
              disabledDescription={disabledDescription}
              primaryText={
                <Ellipsis>
                  <strong>{playlist.name}</strong>
                </Ellipsis>
              }
              secondaryText={secondaryText}
              key={playlist.id}
              name={playlist.id}
              linkName={`Add to ${playlist.default ? 'Default' : 'Other'} Playlist`}
            />
          )
        })}
    </CheckboxList>
  )
}

const CreatePlaylist = ({ sessionId }: { sessionId: string }): JSX.Element => {
  const history = useHistory()
  const { createPlaylist } = usePlaylistCreation()

  const onSubmit = async ({ name, description }: { name: string; description: string }): Promise<void> => {
    await createPlaylist({ name, description, initialItem: sessionId || undefined })
    history.goBack()
  }

  return <UpdatePlaylistForm onSubmit={onSubmit} />
}

function Wrapper({ children, height }: { children: React.ReactNode; height: number }): JSX.Element {
  const atWidth = usePageWidth()
  return (
    <div
      data-testid="playlist-overlay-scroll-container"
      style={atWidth({ default: {}, md: { maxHeight: height, overflowX: 'hidden', overflowY: 'auto' } })}
    >
      {children}
    </div>
  )
}

export const PlaylistManagementOverlay = ({
  sessionId,
  overlay,
  onPlaylistUpdated,
}: {
  sessionId: string
  overlay: ReturnType<typeof useOverlay>
  onPlaylistUpdated?: (playlistIds: string[]) => void
}): JSX.Element => {
  const [t] = useTranslation()
  const { [FLAGS.NEW_PLAYLIST_STATE]: newPlaylistStateFlag } = useFlagsContext()
  const { playlists: nPlaylists, isLoading: nIsLoading } = usePlaylists()
  const { playlists: cPlaylistsRaw } = usePlaylistContainerContext()
  const cPlaylists = cPlaylistsRaw ?? new Map()

  const playlists = newPlaylistStateFlag.isOn ? nPlaylists : [...cPlaylists.values()].filter((p) => !p.parent)
  const isLoading = newPlaylistStateFlag.isOn && nIsLoading
  const canCreatePlaylists = playlists.filter((p) => !p.parent).length < playlistsConfig.playlistLimit

  return (
    <TransitioningDialogOverlay
      {...overlay}
      size="sm"
      title={t('playlists.overlay.heading', 'Add to...')}
      initialLocation="/update"
    >
      <Route
        path="/update"
        footer={
          <DialogOverlayActions>
            <PillButton
              Icon={ContentAdd}
              color="blue"
              disabled={!canCreatePlaylists}
              emphasis="low"
              linkName="Create Playlist"
              data-testid="create-playlist"
              to="/create"
            >
              {t('playlists.collection.create.action', 'Create Playlist')}
            </PillButton>
          </DialogOverlayActions>
        }
      >
        <Wrapper height={150}>
          {isLoading ? <Loading /> : <UpdatePlaylist sessionId={sessionId} onPlaylistUpdated={onPlaylistUpdated} />}
        </Wrapper>
      </Route>
      <Route path="/create" title={t('playlists.create.title', 'Create New Playlist')}>
        {/* The create form is fixed-size content, so it is not wrapped in a scroll
            container — a marginal inner scrollbar rendered next to the Save button and
            caused a glitchy scroll interaction (RT-7111). The dialog handles overflow. */}
        <CreatePlaylist sessionId={sessionId} />
      </Route>
    </TransitioningDialogOverlay>
  )
}
